import React, { useState, useRef, useCallback } from "react";
import Button from "../../../components/controls/button";
import CustomPopover from "../../../components/popover/Popover";
import CustomButton from "../../../components/controls/button/Button";
import { CalendarIcon, FileDownSvg, Cross } from "../../../modules/default-svg";
import CustomInput from "../../../components/controls/input/customInput/CustomInput";
import DropDown from "../../../components/controls/dropdown/DropdownFluent";
import Searchable from "../../../components/controls/select/CustomSelect";
import UserSelect from "../../../components/userSelect/UserSelect";
import { exportFormats, exportTypes, UserSearchRefType } from "../../../helpers/utils";
import exportService from "../../../store/services/export.service";
import searchService from "../../../store/services/search.service";
import { useDispatch } from "react-redux";
import { generalActions } from "../../../store/actions";
import { Download, X } from "lucide-react";
import { debounce } from "lodash";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import Toggle from "../../../components/controls/toggle/Toggle";
import styles from "../../../components/Export/ExportButton.module.scss";

dayjs.extend(isoWeek);

const timesheetPeriodAggregations = [
    { name: "Day", value: 0 },
    { name: "Week", value: 1 },
    { name: "Month", value: 2 },
    { name: "Quarter", value: 3 },
    { name: "Year", value: 4 },
];

const timesheetGroupByOptions = [
    { name: "Resource", value: 0 },
    { name: "Project", value: 1 },
];

const timesheetStatusOptions = [
    { name: "All", value: -1 },
    { name: "Draft", value: 0 },
    { name: "Submitted", value: 1 },
    { name: "Validated", value: 2 },
];

const timesheetDateRangeOptions = [
    { name: "Last Week", value: "last_week" },
    { name: "This Week", value: "this_week" },
    { name: "Next Week", value: "next_week" },
    { name: "Last Month", value: "last_month" },
    { name: "This Month", value: "this_month" },
    { name: "Next Month", value: "next_month" },
    { name: "Overall", value: "overall" },
    { name: "Custom", value: "custom" },
];

function getDateRangeFromPreset(preset) {
    const now = dayjs();
    switch (preset) {
        case "last_week":
            return {
                startDate: now.subtract(1, "week").startOf("isoWeek").format("YYYY-MM-DD"),
                endDate: now.subtract(1, "week").endOf("isoWeek").format("YYYY-MM-DD"),
            };
        case "this_week":
            return {
                startDate: now.startOf("isoWeek").format("YYYY-MM-DD"),
                endDate: now.endOf("isoWeek").format("YYYY-MM-DD"),
            };
        case "next_week":
            return {
                startDate: now.add(1, "week").startOf("isoWeek").format("YYYY-MM-DD"),
                endDate: now.add(1, "week").endOf("isoWeek").format("YYYY-MM-DD"),
            };
        case "last_month":
            return {
                startDate: now.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
                endDate: now.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
            };
        case "this_month":
            return {
                startDate: now.startOf("month").format("YYYY-MM-DD"),
                endDate: now.endOf("month").format("YYYY-MM-DD"),
            };
        case "next_month":
            return {
                startDate: now.add(1, "month").startOf("month").format("YYYY-MM-DD"),
                endDate: now.add(1, "month").endOf("month").format("YYYY-MM-DD"),
            };
        case "overall":
            return { startDate: null, endDate: null };
        case "custom":
            return { startDate: null, endDate: null };
        default:
            return {
                startDate: now.startOf("isoWeek").format("YYYY-MM-DD"),
                endDate: now.endOf("isoWeek").format("YYYY-MM-DD"),
            };
    }
}

function getDateRangeLabel(value) {
    const opt = timesheetDateRangeOptions.find((o) => o.value === value);
    return opt?.name ?? "";
}

function getAvailableDateRangeOptions(hasFilter) {
    return timesheetDateRangeOptions.filter(
        (o) => o.value !== "overall" || hasFilter
    );
}

export default function TimesheetExportButton() {
    const dispatch = useDispatch();
    const today = dayjs();

    const [openPopover, setIsOpenPopover] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Export config
    const [format, setFormat] = useState(0);
    const [type, setType] = useState(0);
    const [groupBy, setGroupBy] = useState(0);
    const [period, setPeriod] = useState(1); // aggregation period
    const [includeFinancials, setIncludeFinancials] = useState(true);
    const [status, setStatus] = useState(2); // default: Validated

    // Date range
    const [dateRangePreset, setDateRangePreset] = useState("this_week");
    const [startDate, setStartDate] = useState(
        today.startOf("isoWeek").format("YYYY-MM-DD")
    );
    const [endDate, setEndDate] = useState(
        today.endOf("isoWeek").format("YYYY-MM-DD")
    );

    // Project filter
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectOptions, setProjectOptions] = useState([]);

    // Resource filter
    const [selectedResource, setSelectedResource] = useState([]);

    const init = () => {
        setFormat(0);
        setType(0);
        setGroupBy(0);
        setPeriod(1);
        setIncludeFinancials(true);
        setStatus(2);
        setDateRangePreset("this_week");
        const range = getDateRangeFromPreset("this_week");
        setStartDate(range.startDate);
        setEndDate(range.endDate);
        setSelectedProject(null);
        setSelectedResource([]);
        setProjectOptions([]);
        setIsOpenPopover(true);
    };

    // Project search
    const searchProjectsHandler = async (searchTerm) => {
        try {
            const response = await searchService.Projects(searchTerm || "");
            if (response && Array.isArray(response)) {
                setProjectOptions(
                    response.map((p) => ({ label: p.name, value: p.id }))
                );
            } else {
                setProjectOptions([]);
            }
        } catch {
            setProjectOptions([]);
        }
    };

    const debouncedProjectSearch = useRef(
        debounce((value) => searchProjectsHandler(value), 300)
    );

    const handleProjectSearch = useCallback((value) => {
        debouncedProjectSearch.current(value);
    }, []);

    const handleProjectChange = (option) => {
        if (option?.value) {
            setSelectedProject({ id: option.value, name: option.label });
        } else {
            setSelectedProject(null);
        }
        // Clear resources when project changes or is cleared
        setSelectedResource([]);
        // If overall was selected and no filter remains, reset to this_week
        if (!option?.value && selectedResource.length === 0 && dateRangePreset === "overall") {
            setDateRangePreset("this_week");
            const range = getDateRangeFromPreset("this_week");
            setStartDate(range.startDate);
            setEndDate(range.endDate);
        }
    };

    const hasFilter = !!selectedProject || selectedResource.length > 0;

    const handleResourceChange = (users) => {
        setSelectedResource(users);
        // If overall was selected and no filter remains after clearing, reset to this_week
        if (users.length === 0 && !selectedProject && dateRangePreset === "overall") {
            setDateRangePreset("this_week");
            const range = getDateRangeFromPreset("this_week");
            setStartDate(range.startDate);
            setEndDate(range.endDate);
        }
    };

    const availableDateRangeOptions = getAvailableDateRangeOptions(hasFilter);

    const handleDateRangePresetChange = (index) => {
        const preset = availableDateRangeOptions[index];
        if (!preset) return;
        setDateRangePreset(preset.value);
        const range = getDateRangeFromPreset(preset.value);
        if (range.startDate) setStartDate(range.startDate);
        if (range.endDate) setEndDate(range.endDate);
    };

    const handleCustomDateChange = (type, date) => {
        const formatted = dayjs(date).format("YYYY-MM-DD");
        if (type === "start") setStartDate(formatted);
        else if (type === "end") setEndDate(formatted);
    };

    const handleExport = () => {
        if (dateRangePreset !== "overall" && (!startDate || !endDate)) {
            dispatch(
                generalActions.showToast({
                    title: "Please select a valid date range.",
                    type: "warning",
                })
            );
            return;
        }

        if (dateRangePreset !== "overall" && dayjs(startDate).isAfter(dayjs(endDate))) {
            dispatch(
                generalActions.showToast({
                    title: "End date must be after start date.",
                    type: "warning",
                })
            );
            return;
        }

        setIsLoading(true);

        const params = {
            Format: format,
            Type: type,
            GroupBy: groupBy,
            Period: period,
            IncludeFinancials: includeFinancials,
        };

        if (status !== -1) {
            params.Status = status;
        }

        if (dateRangePreset !== "overall") {
            params.StartDate = startDate;
            params.EndDate = endDate;
        }

        if (selectedProject?.id) {
            params.ProjectId = selectedProject.id;
        }
        if (selectedResource.length > 0) {
            params.ResourceId = selectedResource.map((r) => r.id).join(",");
        }

        exportService
            .getTimesheetExport(params)
            .then((response) => {
                const contentDisposition = response.headers["content-disposition"];
                let fileName = "Timesheets";
                let fileExtension = format === 1 ? "xlsx" : "csv";

                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (match && match[1]) {
                        fileName = match[1].split(".")[0];
                    }
                }

                const fileType = response.headers.getContentType?.() || response.headers["content-type"];
                if (fileType?.includes("csv")) {
                    fileExtension = "csv";
                } else if (fileType?.includes("spreadsheet")) {
                    fileExtension = "xlsx";
                }

                const blob = new Blob([response.data], { type: fileType });
                const url = window.URL.createObjectURL(blob);

                const a = document.createElement("a");
                a.href = url;
                a.download = `${fileName}.${fileExtension}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                dispatch(
                    generalActions.showToast({
                        title: "Export successful",
                        type: "success",
                    })
                );
            })
            .catch((error) => {
                console.error("Download failed:", error);
                dispatch(
                    generalActions.showToast({
                        title: "Export Failed. Please try again.",
                        type: "error",
                    })
                );
            })
            .finally(() => {
                setIsLoading(false);
                setIsOpenPopover(false);
            });
    };

    return (
        <div>
            <CustomPopover
                icon={
                    <Button
                        btnClass={`d_btn ms-12 d_btn_primary ${styles.exportButton}`}
                        icon={<FileDownSvg />}
                        label="Export"
                        clickHandler={() => init()}
                    />
                }
                minWidth={480}
                PopSurfaceClass="mt-1"
                status={openPopover}
            >
                <div className="text-[#342c82]">
                    <div className="d-flex justify-content-end align-items-center mb-2">
                        <div onClick={() => setIsOpenPopover(false)}>
                            <Cross />
                        </div>
                    </div>
                    <div className="d-flex flex-column">
                        <div className={`${styles.filterPopoverBody} FilterPopoverBody`}>
                            <div className="FilterPopoverBodyScroll">
                                {/* Project */}
                                <div className="d-flex row align-items-center mb-3">
                                    <div className="col-3">Project</div>
                                    <div className="col-9 align-items-center">
                                        <Searchable
                                            placeholder="Search project..."
                                            SelectText="Project"
                                            searchText={handleProjectSearch}
                                            options={projectOptions}
                                            onChange={handleProjectChange}
                                            defaultVal={
                                                selectedProject
                                                    ? { label: selectedProject.name, value: selectedProject.id }
                                                    : null
                                            }
                                        />
                                    </div>
                                </div>

                                {/* Resource */}
                                <div className="d-flex row align-items-center mb-3">
                                    <div className="col-3">Resource</div>
                                    <div className="col-9 align-items-center">
                                        <UserSelect
                                            multiple={true}
                                            refType={
                                                selectedProject
                                                    ? UserSearchRefType.Project
                                                    : null
                                            }
                                            refId={selectedProject?.id || null}
                                            isAdminSearch={!selectedProject}
                                            value={selectedResource}
                                            onChange={handleResourceChange}
                                            buttonLabel="Add Resources"
                                            selectedUsersDisplayMode="avatar-only"
                                            selectedUsersDisplayLimit={3}
                                        />
                                    </div>
                                </div>
                                {/* Status */}
                                <div className="d-flex row align-items-center mb-3">
                                    <div className="col-3">Status</div>
                                    <div className="col-9 align-items-center">
                                        <DropDown
                                            dropDownwd="w-70"
                                            showLabel={false}
                                            options={timesheetStatusOptions}
                                            defaultVal={timesheetStatusOptions.find((s) => s.value === status)?.name || ""}
                                            onChange={(e) => setStatus(e)}
                                        />
                                    </div>
                                </div>
                                {/* Date Range */}
                                <div className="d-flex row align-items-center mb-3">
                                    <div className="col-3">Date Range</div>
                                    <div className="col-9 align-items-center">
                                        <DropDown
                                            dropDownwd="w-70"
                                            showLabel={false}
                                            options={availableDateRangeOptions.map((o, i) => ({ name: o.name, value: i }))}
                                            defaultVal={getDateRangeLabel(dateRangePreset)}
                                            onChange={handleDateRangePresetChange}
                                        />
                                        {dateRangePreset === "custom" && (
                                            <div className={styles.options}>
                                                <div className={styles.rangeDateWrapper}>
                                                    <div className="text-xs h-[43px] flex items-center justify-center gap-1">
                                                        <CustomInput
                                                            type="date"
                                                            width="105px"
                                                            DateStyleClass="date-input DatePickerInput"
                                                            DateStyles={{ fontSize: "14px" }}
                                                            maxDate={endDate}
                                                            handleChange={(value) =>
                                                                handleCustomDateChange("start", value)
                                                            }
                                                            value={startDate || today.format("YYYY-MM-DD")}
                                                        />
                                                        <span className="text-gray-500 text-sm">-</span>
                                                        <CustomInput
                                                            type="date"
                                                            width="105px"
                                                            DateStyleClass="date-input DatePickerInput"
                                                            DateStyles={{ fontSize: "14px" }}
                                                            minDate={startDate}
                                                            handleChange={(value) =>
                                                                handleCustomDateChange("end", value)
                                                            }
                                                            value={endDate || today.format("YYYY-MM-DD")}
                                                        />
                                                    </div>
                                                    <CalendarIcon />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* Format & Type */}
                                <div className="d-flex row align-items-center mb-3">
                                    <div className="col-3">Format</div>
                                    <div className="col-3 align-items-center">
                                        <DropDown
                                            dropDownwd="w-70"
                                            showLabel={false}
                                            options={exportFormats}
                                            defaultVal={exportFormats.find((f) => f.value === format)?.name || ""}
                                            onChange={(e) => setFormat(e)}
                                        />
                                    </div>
                                    <div className="col-2">Type</div>
                                    <div className="col-4 align-items-center">
                                        <DropDown
                                            dropDownwd="w-70"
                                            showLabel={false}
                                            options={exportTypes}
                                            defaultVal={exportTypes.find((t) => t.value === type)?.name || ""}
                                            onChange={(e) => setType(e)}
                                        />
                                    </div>
                                </div>

                                {/* Group By & Aggregation Period */}
                                <div className="d-flex row align-items-center mb-3">
                                    <div className="col-3">Group By</div>
                                    <div className="col-4 align-items-center">
                                        <DropDown
                                            dropDownwd="w-70"
                                            showLabel={false}
                                            options={timesheetGroupByOptions}
                                            defaultVal={timesheetGroupByOptions.find((g) => g.value === groupBy)?.name || ""}
                                            onChange={(e) => setGroupBy(e)}
                                        />
                                    </div>
                                    <div className="col-4 align-items-center">
                                        <DropDown
                                            dropDownwd="w-70"
                                            showLabel={false}
                                            options={timesheetPeriodAggregations}
                                            defaultVal={timesheetPeriodAggregations.find((p) => p.value === period)?.name || ""}
                                            onChange={(e) => setPeriod(e)}
                                        />
                                    </div>
                                </div>

                                {/* Include Financials */}
                                <div className="d-flex row align-items-center mb-3">
                                    <div className="col-3">Financials</div>
                                    <div className="col-9 align-items-center">
                                        <Toggle
                                            label="Include Cost & Bill columns"
                                            checked={includeFinancials}
                                            handleChange={(val) => setIncludeFinancials(val)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-end gap-2 justify-content-end FilterPopoverFooter px-[10px]">
                        <CustomButton
                            btnClass="d_btn d_btn_white d_btn_whiteXsm"
                            label="Cancel"
                            icon={<X size={16} />}
                            clickHandler={() => setIsOpenPopover(false)}
                        />
                        <CustomButton
                            btnClass="d_btn d_btn_primary d_btn_primaryXsm"
                            label="Export"
                            icon={<Download size={16} />}
                            clickHandler={handleExport}
                            showLoading={isLoading}
                        />
                    </div>
                </div>
            </CustomPopover>
        </div>
    );
}
